import React from 'react';
import { Button, Drawer } from 'rsuite';

/**
 * Returns the FilterPanel drawer's scroll container. Pass it to the
 * `container` prop of any RSuite picker (TagPicker / SelectPicker /
 * DateRangePicker) used inside the panel, together with `preventOverflow`,
 * so the picker's dropdown menu repositions when the filter list scrolls.
 */
export type FilterContainer = () => HTMLElement;

type FilterPanelProps = {
  /** Number of filters set away from their default — shown on the trigger. */
  activeCount: number;
  /** Reset-all handler, invoked from the panel footer. */
  onReset: () => void;
  /**
   * Filter controls, as a render function receiving the drawer's scroll
   * container. Wrap each control in a `FilterField` and pass `container`
   * (plus `preventOverflow`) to every picker inside.
   */
  children: (container: FilterContainer) => React.ReactNode;
};

/**
 * Toolbar "Фільтри" control: a ghost button that opens a right-side Drawer
 * holding the page's filter controls.
 *
 * The Drawer is used instead of a popover on purpose. The Drawer body is a
 * stable, full-height scroll container, so picker menus rendered inside it
 * (via the `container` prop) stay anchored to their trigger while the list
 * scrolls — a popover with internal scroll leaves portalled menus behind.
 *
 * @param props
 */
export default function FilterPanel({
  activeCount,
  onReset,
  children,
}: FilterPanelProps) {
  const [open, setOpen] = React.useState(false);
  const bodyRef = React.useRef<HTMLDivElement>(null);

  // RSuite resolves `container` lazily — only when a picker menu opens, by
  // which point the drawer body is mounted — so returning the ref is safe.
  const getContainer = React.useCallback<FilterContainer>(
    () => bodyRef.current as HTMLElement,
    []
  );

  return (
    <>
      <Button
        appearance="ghost"
        className="filter-panel-trigger"
        onClick={() => setOpen(true)}
      >
        Фільтри
        {activeCount > 0 && (
          <span className="filter-panel-trigger__count">{activeCount}</span>
        )}
      </Button>
      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        size="xs"
        className="filter-panel-drawer"
      >
        <Drawer.Header>
          <Drawer.Title>Фільтри</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body ref={bodyRef} className="filter-panel__body">
          {children(getContainer)}
        </Drawer.Body>
        <Drawer.Actions>
          {activeCount > 0 && (
            <Button appearance="link" onClick={onReset}>
              Скинути фільтри
            </Button>
          )}
          <Button appearance="primary" onClick={() => setOpen(false)}>
            Готово
          </Button>
        </Drawer.Actions>
      </Drawer>
    </>
  );
}

type FilterFieldProps = {
  /** Label rendered above the control. */
  label?: React.ReactNode;
  /** Optional helper text rendered below the control. */
  hint?: React.ReactNode;
  children: React.ReactNode;
};

/**
 * One labelled row inside a FilterPanel — a label, a control, and an
 * optional hint. Replaces the ad-hoc field markup the pages used to repeat.
 *
 * @param props
 */
export function FilterField({ label, hint, children }: FilterFieldProps) {
  return (
    <div className="filter-panel__field">
      {label ? <label className="filter-panel__label">{label}</label> : null}
      {children}
      {hint ? <span className="filter-panel__hint">{hint}</span> : null}
    </div>
  );
}
